import React from 'react';
import PageHero from '../components/PageHero';

export default function Partners() {
  return (
    <>
      <PageHero
        eyebrow="For capital partners"
        title="Deal flow you can't get"
        emphasis="anywhere else."
        lede="OfficerCapital is the only marketplace dedicated to security guard companies. Every listing is a verified operating business with audited financials. Every owner is on the platform because they want a conversation. Apply for access below."
      >
        <a href="#apply" className="oc-btn oc-btn--primary">Apply for access</a>
        <a href="/pricing" className="oc-btn oc-btn--link">See partner pricing →</a>
      </PageHero>

      {/* WHAT YOU GET */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">What's on the platform</div>
              <h2 className="oc-h2">
                Real deals from real <em className="oc-em">owners</em>.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              Listings on OfficerCapital come from owners actively seeking capital — not curiosity browsers,
              not data leaks, not stale broker books. Every listing has been reviewed and the financials verified.
            </p>
          </div>

          <div className="oc-grid-3">
            <PartnerTypeCard
              label="If you're a lender"
              body="Specialty factoring, working capital, asset-based lending for guard companies. Owners come to OfficerCapital specifically because they want lenders who understand the receivables cycle."
              cta="See lender flow"
            />
            <PartnerTypeCard
              label="If you're an investor"
              body="Growth equity, recap, minority investment in guard companies. Owners on the investor track are typically $5M+ in revenue and looking for sector-specialized capital."
              cta="See investor flow"
            />
            <PartnerTypeCard
              label="If you're an acquirer"
              body="Strategic and financial buyers building security platforms. Listings range from regional independents to multi-state operators. Most are off-market — you won't see them anywhere else."
              cta="See acquirer flow"
              featured
            />
          </div>
        </div>
      </section>

      {/* HOW PARTNERSHIP WORKS */}
      <section className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'start' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">How partnership works</div>
              <h2 className="oc-h2">
                Apply once. Browse, unlock, <em className="oc-em">close</em>.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                Approval typically takes three to five business days. Once you're in, you can browse anonymized listings,
                pay to unlock the ones you want to evaluate, and message owners directly once they approve your access request.
                There is no minimum deal commitment and no ongoing reporting requirement.
              </p>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack">
                <Step n="01" t="Apply" b="Firm details, fund profile, prior deal history, references." />
                <hr className="oc-rule" />
                <Step n="02" t="Manual review" b="We review every application by hand. Three to five business days." />
                <hr className="oc-rule" />
                <Step n="03" t="Subscribe" b="$200 per month for marketplace access." />
                <hr className="oc-rule" />
                <Step n="04" t="Unlock and engage" b="$500 per listing for lenders and investors. $1,500 for acquirers." />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE EXPECT */}
      <section className="oc-section">
        <div className="oc-shell">
          <div className="oc-grid-1-2 oc-mb-5">
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">What we expect from partners</div>
              <h2 className="oc-h2">
                The <em className="oc-em">terms</em>, plainly.
              </h2>
            </div>
            <p className="oc-lede oc-max-520" style={{ paddingTop: 14, fontSize: 16 }}>
              The platform works because owners trust it. That trust is what we're protecting.
              These are the rules — they're not negotiable, and we enforce them.
            </p>
          </div>

          <div className="oc-grid-3">
            <RuleCard
              label="No external sharing"
              body="Listing data — financials, customer detail, owner identity — does not leave your firm. Sharing with parties outside your organization is grounds for immediate removal."
            />
            <RuleCard
              label="Honest profile"
              body="Your firm profile, deal history, and references must be accurate. Misrepresentation is grounds for immediate removal and forfeiture of unlock fees."
            />
            <RuleCard
              label="No off-platform circumvention"
              body="Don't approach owners through other channels using information learned on the platform. Owners can revoke approval at any time."
            />
          </div>
        </div>
      </section>

      {/* APPLICATION CTA */}
      <section id="apply" className="oc-section oc-section--white">
        <div className="oc-shell">
          <div className="oc-grid-2" style={{ alignItems: 'center' }}>
            <div>
              <div className="oc-eyebrow-gray oc-mb-2">Ready to apply?</div>
              <h2 className="oc-h2">
                Submit your firm profile to <em className="oc-em">get started</em>.
              </h2>
              <p className="oc-body oc-mt-3 oc-max-440">
                Applications take about 10 minutes to complete. We respond within five business days.
              </p>
              <div className="oc-mt-4 oc-row" style={{ gap: 8 }}>
                <a href="/apply" className="oc-btn oc-btn--primary">Start application</a>
                <a href="mailto:partners@officercapital.com" className="oc-btn oc-btn--link">
                  partners@officercapital.com →
                </a>
              </div>
            </div>
            <div className="oc-border-l">
              <div className="oc-stack-sm">
                <ChecklistItem label="Firm legal name &amp; entity type" />
                <ChecklistItem label="Primary investment thesis or lending profile" />
                <ChecklistItem label="Prior deal history (last 24 months)" />
                <ChecklistItem label="Two professional references" />
                <ChecklistItem label="AUM or fund size, if applicable" />
                <ChecklistItem label="Designated platform contact" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function PartnerTypeCard({ label, body, cta, featured }) {
  return (
    <div
      className="oc-card"
      style={featured ? { borderColor: 'var(--oc-green)', borderWidth: '1px' } : undefined}
    >
      <div className="oc-eyebrow-gray oc-mb-2">{label}</div>
      <p className="oc-body-sm" style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 16 }}>{body}</p>
      <hr className="oc-rule oc-mb-2" />
      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--oc-ink)' }}>{cta} →</span>
    </div>
  );
}

function Step({ n, t, b }) {
  return (
    <div>
      <div className="oc-row" style={{ gap: 12, alignItems: 'baseline', marginBottom: 6 }}>
        <span className="oc-step__num">{n}</span>
        <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--oc-ink)' }}>{t}</span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--oc-gray)', lineHeight: 1.55, paddingLeft: 32 }}>{b}</div>
    </div>
  );
}

function RuleCard({ label, body }) {
  return (
    <div className="oc-card">
      <div className="oc-eyebrow-gray oc-mb-2">{label}</div>
      <p className="oc-body-sm" style={{ fontSize: 13, lineHeight: 1.55 }}>{body}</p>
    </div>
  );
}

function ChecklistItem({ label }) {
  return (
    <div className="oc-row" style={{ gap: 10, fontSize: 14, color: 'var(--oc-body)' }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--oc-green)" strokeWidth="2" style={{ flexShrink: 0 }}>
        <path d="M5 12 l4 4 l10-10" />
      </svg>
      <span dangerouslySetInnerHTML={{ __html: label }} />
    </div>
  );
}
